import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import { Container } from '@material-ui/core';

import { roomTypeQuery, roomTypeService, propertyQuery } from '@lib/state';
import {
  useObservable,
  Section,
  FormHub,
  useFormEvents,
  usePageTitle,
  usePropertyAmenities,
} from '@lib/common';
import {
  roomRatePlanQuery,
  roomRatePlanService,
  RoomRatePlanForms,
  corporateAccountService,
  corporateAccountQuery,
  AdditionalProperty,
} from 'app/state';
import { prepareFormValues, RatePlanForm, RatePlanFormFields } from '../components';
import { BreadcrumbBackButton } from 'app/shared';

export const CreateRoomRatePlanPage: React.FC<RouteComponentProps> = props => {
  const queryString = props.location.search;
  const params = new URLSearchParams(queryString);
  const corporateAccountId = params.get('corporateAccountId') ?? undefined;

  const [selectedRates, setSelectedRates] = useState<AdditionalProperty[]>([]);

  usePageTitle('New Room Rate');

  const propertyId = useObservable(propertyQuery.activeProperty, 'async')?.id;
  const customerId = useObservable(propertyQuery.activeProperty)?.customerId;

  const roomTypes = useObservable(roomTypeQuery.propertyRoomTypes);
  const corporateAccounts = useObservable(corporateAccountQuery.searchResults);
  const roomRatePlan = useObservable(roomRatePlanQuery.activeRatePlan, 'async');
  const amenities = usePropertyAmenities(propertyId);
  const corporateAccount = useObservable(corporateAccountQuery.activeAccount);

  useEffect(() => {
    if (propertyId) roomTypeService.getPropertyRoomTypes(propertyId);
  }, [propertyId]);

  useEffect(() => {
    if (customerId) corporateAccountService.getCorporateAccounts(customerId, {});
  }, [customerId]);

  useEffect(() => {
    if (corporateAccountId) corporateAccountService.getCorporateAccountById(corporateAccountId);
    return () => corporateAccountService.selectCorporateAccount();
  }, [corporateAccountId]);

  const [{ status, error }, reset] = useFormEvents(RoomRatePlanForms.Create);

  const defaultValues = useMemo(
    () =>
      prepareFormValues(amenities, {
        priority: 10,
        roomTypeId: '',
        name: corporateAccount?.name ?? '',
        affiliation: {
          corporateAccountId: corporateAccount?.id ?? '',
        },
      }),
    [amenities, corporateAccount]
  );

  const sectionTitle = useMemo(
    () =>
      corporateAccount
        ? `Create Room Rate Plan for ${corporateAccount.name} (Corporate Account)`
        : 'Create Room Rate Plan',
    [corporateAccount]
  );

  if (roomRatePlan != null) return <Redirect to={`/rates/${roomRatePlan.id}`} />;

  if (!propertyId || !defaultValues) return null;

  const onSubmit = (values: RatePlanFormFields) =>
    roomRatePlanService.createPlan(
      {
        ...values,
        propertyId,
        corporateAccountId: !!values.corporateAccountId ? values.corporateAccountId : undefined,
        amenities: values.amenities.filter(a => a.available),
      },
      selectedRates
    );
  return (
    <Container maxWidth="lg">
      <BreadcrumbBackButton destination="/rates" />
      <Section title={sectionTitle} textAlign="left">
        <FormHub onSubmit={onSubmit} formOptions={{ defaultValues }}>
          <RatePlanForm
            plan={roomRatePlan}
            roomTypes={roomTypes}
            corporateAccounts={corporateAccounts}
            status={status}
            error={error}
            resetFormUI={reset}
            setSelectedRates={setSelectedRates}
            selectedRates={selectedRates}
            corporateAccount={corporateAccount}
          />
        </FormHub>
      </Section>
    </Container>
  );
};
